import React, { createContext, useContext, useRef, ReactNode } from "react";
import { useSelector } from "react-redux";
import { useWebRTCCall, CallState, CallUser } from "./useWebRTCCall";
import {
  getCallingSocket,
  getSocket,
  initializeCallingSocket,
  initializeSocket,
} from "./chatServices";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GlobalCallContextValue {
  callState: CallState;
  isMuted: boolean;
  callDuration: number;
  needsManualPlay: boolean;
  remoteAudioRef: React.RefObject<HTMLAudioElement>;
  startCall: (targetUser: CallUser, channelId: string) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  hangup: () => void;
  toggleMute: () => void;
  manualPlay: () => void;
  formatDuration: (s: number) => string;
  currentUserId: string;
  // For Chat.tsx to register a local message-update handler (optional)
  onCallMessageRef: React.MutableRefObject<
    | ((
        channelId: string,
        status: "missed" | "answered" | "rejected",
        duration?: number,
        callerId?: string,
      ) => void)
    | null
  >;
}

const GlobalCallContext = createContext<GlobalCallContextValue | null>(null);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDurationHelper(s: number): string {
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const GlobalCallProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const _user = useSelector((state: any) => state?.user?.user);
  const currentUserId = String(_user?.userDetails?.id ?? "");
  const currentUserName = _user?.userDetails?.name ?? "You";
  const currentUserAvatar = _user?.userDetails?.profile_pic ?? "";

  // Chat.tsx may register a local handler to update its own UI state
  const onCallMessageRef = useRef<
    | ((
        channelId: string,
        status: "missed" | "answered" | "rejected",
        duration?: number,
        callerId?: string,
      ) => void)
    | null
  >(null);

  // Ensure sockets are initialized at app startup
  initializeCallingSocket();
  initializeSocket();

  /**
   * Central call-message handler.
   *
   * This is the authoritative source of truth — always emits the call record
   * to the backend via the chat socket so it gets saved to the DB, regardless
   * of whether the Chat component is currently mounted.
   *
   * Additionally, if Chat is mounted it notifies it via onCallMessageRef so the
   * local message list can update immediately without a round-trip.
   */
  const handleCallMessage = (
    channelId: string,
    status: "missed" | "answered" | "rejected",
    duration?: number,
    initiatorId?: string,
  ) => {
    const isCaller = String(initiatorId || "") === String(currentUserId);
    
    const texts: Record<string, string> = {
      missed: isCaller ? "no answer" : "Miss call",
      rejected: isCaller ? "Call Rejected" : "You declined the call",
      answered: `Audio call · ${formatDurationHelper(duration ?? 0)}`,
    };
    const text = texts[status] ?? "Call ended";
    
    // Explicit callerId for the record: if we didn't get one, assume we are the one reporting it
    const finalCallerId = initiatorId || currentUserId;

    // ✅ Always emit via chat socket regardless of Chat component mount state
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit("send-message", {
        channelId,
        content: text,
        attachments: [],
        type: "call",
        callDetails: {
          callType: "audio",
          callStatus: status,
          duration,
          callerId: finalCallerId,
        },
      });
    }

    // ✅ If Chat is mounted, notify it so it refreshes its local state instantly
    if (onCallMessageRef.current) {
      onCallMessageRef.current(channelId, status, duration, finalCallerId);
    }
  };

  const webrtc = useWebRTCCall({
    callingSocket: getCallingSocket(),
    chatSocket: getSocket(),
    currentUserId,
    currentUserName,
    currentUserAvatar,
    onCallMessage: handleCallMessage,
  });

  return (
    <GlobalCallContext.Provider value={{ ...webrtc, onCallMessageRef, currentUserId }}>
      {children}
    </GlobalCallContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useGlobalCall() {
  const ctx = useContext(GlobalCallContext);
  if (!ctx)
    throw new Error("useGlobalCall must be used inside GlobalCallProvider");
  return ctx;
}
